import { idSchema } from '@/lib/utils'
import { z } from 'zod/v4'
import type { CustomPage, CustomPageInput } from './custom-pages.types'

// Limits mirror `SaveCustomPageRequest` and `CustomPage::SLUG_PATTERN`.
export const CUSTOM_PAGE_META_DESCRIPTION_MAX_LENGTH = 300
const CUSTOM_PAGE_HTML_MAX_LENGTH = 200_000
const CUSTOM_PAGE_TITLE_MAX_LENGTH = 120
const CUSTOM_PAGE_SLUG_MAX_LENGTH = 80
const CUSTOM_PAGE_SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/

export const customPageRouteParamsSchema = z.object({
  customPageId: idSchema('custom page'),
})

export const customPageFormSchema = z.object({
  title: z
    .string()
    .trim()
    .min(1, 'Enter a page title.')
    .max(
      CUSTOM_PAGE_TITLE_MAX_LENGTH,
      `Keep the title to ${CUSTOM_PAGE_TITLE_MAX_LENGTH} characters or fewer.`
    ),
  slug: z
    .string()
    .trim()
    .min(1, 'Enter a page address.')
    .max(
      CUSTOM_PAGE_SLUG_MAX_LENGTH,
      `Keep the page address to ${CUSTOM_PAGE_SLUG_MAX_LENGTH} characters or fewer.`
    )
    .regex(
      CUSTOM_PAGE_SLUG_PATTERN,
      'Use lowercase letters, numbers and single hyphens for the page address.'
    ),
  meta_description: z
    .string()
    .trim()
    .max(
      CUSTOM_PAGE_META_DESCRIPTION_MAX_LENGTH,
      `Keep the meta description to ${CUSTOM_PAGE_META_DESCRIPTION_MAX_LENGTH} characters or fewer.`
    ),
  is_published: z.boolean(),
  html: z
    .string()
    .refine((html) => html.trim() !== '', 'Paste the page HTML.')
    .refine(
      (html) => html.length <= CUSTOM_PAGE_HTML_MAX_LENGTH,
      'The HTML must be 200,000 characters or fewer.'
    ),
})

export type CustomPageFormValues = z.input<typeof customPageFormSchema>

/** New pages start hidden so staff can check them before shoppers see them. */
export const newCustomPageFormValues: CustomPageFormValues = {
  title: '',
  slug: '',
  meta_description: '',
  is_published: false,
  html: '',
}

export function customPageFormValues(customPage: CustomPage): CustomPageFormValues {
  return {
    title: customPage.title,
    slug: customPage.slug,
    meta_description: customPage.meta_description ?? '',
    is_published: customPage.is_published,
    html: customPage.html,
  }
}

/** The save request; a blank meta description falls back to the start of the page. */
export function customPageInput(values: CustomPageFormValues): CustomPageInput {
  return {
    title: values.title.trim(),
    slug: values.slug.trim(),
    html: values.html,
    meta_description: values.meta_description.trim() || null,
    is_published: values.is_published,
  }
}

/** Keeps a typed page address to lowercase letters, numbers and hyphens (spaces become hyphens). */
export function normalizeCustomPageSlugInput(value: string): string {
  return value
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
}
